// src/components/GRNPanel.tsx
// Step 3 — GRN recorder modal (goods received note)
// Step 4 — 3-way match panel (PO ↔ GRN ↔ Invoice)

import { useState, useEffect } from 'react'
import {
  X, Camera, CheckCircle, AlertTriangle, Package,
  Clock, User, FileText, ArrowRight, Info, Truck,
  ChevronDown, ChevronUp, Lock, ClipboardList,
} from 'lucide-react'
import {
  GoodsReceivedNote, ThreeWayMatchResult,
  GRN_STATUS_META, MATCH_STATUS_META, GRNItemCondition,
  addDeliveryConfirmation, DeliveryChecklistLine,
  DeliveryLineStatus, DeliveryLocation,
} from '../utils/mockDataGRN'
import { MOCK_TASKS, MOCK_BOM } from '../utils/mockDataDrawings'

// ── Shared formatters ─────────────────────────────────────────────

function fmtAUD(n: number) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(n)
}
function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })
}

// ─────────────────────────────────────────────────────────────────
// STEP 3 — GRN RECORDER MODAL
// ─────────────────────────────────────────────────────────────────

interface GRNRecorderModalProps {
  po: Record<string, any>
  job: Record<string, any> | null
  existingGRN?: GoodsReceivedNote
  onClose: () => void
}

const CONDITION_META: Record<GRNItemCondition, { label: string; bg: string; text: string }> = {
  GOOD:     { label: 'Good condition',  bg: 'bg-green-100',  text: 'text-green-700'  },
  PARTIAL:  { label: 'Partial / short', bg: 'bg-amber-100',  text: 'text-amber-700'  },
  DAMAGED:  { label: 'Damaged',         bg: 'bg-orange-100', text: 'text-orange-700' },
  REJECTED: { label: 'Rejected',        bg: 'bg-red-100',    text: 'text-red-700'    },
}

export function GRNRecorderModal({ po, job, existingGRN, onClose }: GRNRecorderModalProps) {
  const [mode, setMode] = useState<'view' | 'record'>(existingGRN ? 'view' : 'record')
  const [deliveryDate, setDeliveryDate] = useState(existingGRN?.deliveryDate ?? new Date().toISOString().split('T')[0])
  const [docketRef, setDocketRef]       = useState(existingGRN?.deliveryDocketRef ?? '')
  const [location, setLocation]         = useState(existingGRN?.deliveryLocation ?? job ? `${job?.jobNumber} site — main laydown area` : '')
  const [notes, setNotes]               = useState(existingGRN?.notes ?? '')
  const [photoCount, setPhotoCount]     = useState(existingGRN?.photoCount ?? 0)
  const [submitted, setSubmitted]       = useState(false)

  // Gap 4 — Task allocation: which DrawingTask should these materials go to?
  const jobTasks = job ? MOCK_TASKS.filter(t => t.jobId === job.id && t.level !== 1) : []
  // Pre-select by auto-matching PO description to a BOM item's task
  const autoMatchedTask = jobTasks.find(t =>
    t.bomItemIds.some(bid => {
      const bom = MOCK_BOM.find(b => b.id === bid)
      return bom && po.description && bom.description.toLowerCase().includes(
        po.description.toLowerCase().slice(0, 20)
      )
    })
  )
  const [allocatedTaskId, setAllocatedTaskId] = useState<string>(autoMatchedTask?.id ?? '')

  // Line items seeded from PO description (or from existing GRN)
  const defaultLine = {
    description: po.description ?? 'Supply as per purchase order',
    orderedQty:  1,
    receivedQty: existingGRN?.lineItems[0]?.receivedQty ?? 1,
    unit:        'item',
    condition:   'GOOD' as GRNItemCondition,
    notes:       '',
  }
  const [lines, setLines] = useState(
    existingGRN
      ? existingGRN.lineItems.map(l => ({
          description: l.description,
          orderedQty:  l.orderedQty,
          receivedQty: l.receivedQty,
          unit:        l.unit,
          condition:   l.condition,
          notes:       l.notes ?? '',
        }))
      : [defaultLine]
  )

  useEffect(() => {
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handle)
    return () => window.removeEventListener('keydown', handle)
  }, [onClose])

  const hasDiscrepancy = lines.some(l => l.receivedQty < l.orderedQty || l.condition !== 'GOOD')
  const canSubmit = docketRef.trim().length > 0 && lines.every(l => l.receivedQty >= 0)

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">GRN recorded</h3>
          <p className="text-sm text-gray-600 mb-1">
            <span className="font-semibold">GRN-{po.poNumber ?? po.id}-001</span> has been submitted.
          </p>
          {hasDiscrepancy && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 text-left flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Delivery discrepancy recorded. This will be flagged in the 3-way match when the invoice is processed.
            </div>
          )}
          <button onClick={onClose} className="mt-6 btn-primary w-full">Done</button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl">

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b bg-gray-50 rounded-t-2xl">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <Truck className="w-4 h-4 text-[#2E75B6]" />
              <span className="font-semibold text-gray-900">
                {mode === 'view' ? 'Goods Received Note' : 'Record goods received'}
              </span>
              {existingGRN && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${GRN_STATUS_META[existingGRN.status].bg} ${GRN_STATUS_META[existingGRN.status].text}`}>
                  {GRN_STATUS_META[existingGRN.status].label}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {po.poNumber ?? po.id} · {po.supplier} · {job?.jobNumber ?? ''}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {existingGRN && mode === 'view' && (
              <button onClick={() => setMode('record')} className="btn-secondary text-xs">Amend GRN</button>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:bg-gray-200 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* View mode — read-only summary */}
          {mode === 'view' && existingGRN && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['GRN number',      existingGRN.grnNumber],
                  ['Delivery date',   fmtDate(existingGRN.deliveryDate)],
                  ['Docket ref',      existingGRN.deliveryDocketRef],
                  ['Delivery site',   existingGRN.deliveryLocation],
                  ['Received by',     existingGRN.receivedByName],
                  ['Photos taken',    String(existingGRN.photoCount)],
                ].map(([label, value]) => (
                  <div key={label} className="bg-gray-50 rounded-xl px-3 py-2">
                    <div className="text-xs text-gray-400">{label}</div>
                    <div className="font-medium text-gray-900 text-sm">{value}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Line items received</div>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50 border-b">
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Description</th>
                      <th className="text-right px-2 py-2 font-medium text-gray-600 w-16">Ordered</th>
                      <th className="text-right px-2 py-2 font-medium text-gray-600 w-20">Received</th>
                      <th className="text-left px-2 py-2 font-medium text-gray-600 w-28">Condition</th>
                    </tr></thead>
                    <tbody>
                      {existingGRN.lineItems.map((item, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2 text-gray-800">
                            {item.description}
                            {item.notes && <div className="text-[10px] text-amber-600 mt-0.5">{item.notes}</div>}
                          </td>
                          <td className="px-2 py-2 text-right tabular-nums text-gray-500">{item.orderedQty} {item.unit}</td>
                          <td className={`px-2 py-2 text-right tabular-nums font-semibold ${item.receivedQty < item.orderedQty ? 'text-red-600' : 'text-green-700'}`}>
                            {item.receivedQty} {item.unit}
                          </td>
                          <td className="px-2 py-2">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${CONDITION_META[item.condition].bg} ${CONDITION_META[item.condition].text}`}>
                              {CONDITION_META[item.condition].label}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {existingGRN.notes && (
                <div className="text-xs text-gray-600 bg-gray-50 rounded-xl px-4 py-3 italic">{existingGRN.notes}</div>
              )}
              {existingGRN.verifiedByName && (
                <div className="text-xs text-green-700 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5" />
                  Verified by {existingGRN.verifiedByName} on {fmtDate(existingGRN.verifiedAt)}
                </div>
              )}
            </div>
          )}

          {/* Record mode — entry form */}
          {mode === 'record' && (
            <>
              {/* PO summary */}
              <div className="bg-[#1F3864] rounded-xl px-4 py-3 text-white flex items-center justify-between">
                <div>
                  <div className="text-xs text-blue-300">Purchase order</div>
                  <div className="font-semibold">{po.poNumber ?? po.id}</div>
                  <div className="text-xs text-blue-300 mt-0.5">{po.supplier}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-blue-300">PO value</div>
                  <div className="text-lg font-bold">{fmtAUD(po.totalExGST ?? 0)}</div>
                  <div className="text-[10px] text-blue-400">ex GST</div>
                </div>
              </div>

              {/* Delivery details */}
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Delivery details</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Delivery date <span className="text-red-500">*</span></label>
                    <input type="date" className="input text-sm" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Supplier delivery docket ref <span className="text-red-500">*</span></label>
                    <input className="input text-sm" placeholder="e.g. OLX-DEL-2026-441-A" value={docketRef} onChange={e => setDocketRef(e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Delivery location on site</label>
                    <input className="input text-sm" placeholder="e.g. Gate 2 — materials laydown area" value={location} onChange={e => setLocation(e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Line items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Items received</div>
                  <button
                    onClick={() => setLines(prev => [...prev, { ...defaultLine, receivedQty: 0 }])}
                    className="text-xs text-[#2E75B6] hover:underline"
                  >+ Add line</button>
                </div>
                <div className="space-y-2">
                  {lines.map((line, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl p-3 space-y-2">
                      <div className="text-xs font-medium text-gray-800">{line.description}</div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Ordered</label>
                          <div className="text-sm font-semibold text-gray-600">{line.orderedQty} {line.unit}</div>
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Received <span className="text-red-500">*</span></label>
                          <input
                            type="number" step="0.01" min="0"
                            className={`input text-sm ${line.receivedQty < line.orderedQty ? 'border-amber-400 bg-amber-50' : ''}`}
                            value={line.receivedQty}
                            onChange={e => setLines(prev => prev.map((l, j) => j === i ? { ...l, receivedQty: parseFloat(e.target.value) || 0 } : l))}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] text-gray-500 mb-1">Condition</label>
                          <select
                            className="input text-xs"
                            value={line.condition}
                            onChange={e => setLines(prev => prev.map((l, j) => j === i ? { ...l, condition: e.target.value as GRNItemCondition } : l))}
                          >
                            {(Object.keys(CONDITION_META) as GRNItemCondition[]).map(c => (
                              <option key={c} value={c}>{CONDITION_META[c].label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      {(line.receivedQty < line.orderedQty || line.condition !== 'GOOD') && (
                        <input
                          className="input text-xs"
                          placeholder="Discrepancy note (required for short or damaged deliveries)…"
                          value={line.notes}
                          onChange={e => setLines(prev => prev.map((l, j) => j === i ? { ...l, notes: e.target.value } : l))}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Discrepancy warning */}
              {hasDiscrepancy && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-start gap-2 text-xs text-amber-800">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  Discrepancy noted. This will automatically create a QUANTITY_VARIANCE flag in the 3-way match when the invoice is received. Add notes above to explain each discrepancy.
                </div>
              )}

              {/* Photo evidence */}
              <div>
                <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Photo evidence</div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 border border-dashed border-gray-300 rounded-xl p-3 text-center">
                    <Camera className="w-5 h-5 text-gray-300 mx-auto mb-1" />
                    <div className="text-xs text-gray-400">Attach delivery photos</div>
                    <div className="text-[10px] text-gray-300 mt-0.5">Docket, condition, quantities</div>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <button onClick={() => setPhotoCount(p => p + 1)} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold">+</button>
                    <span className="text-sm font-bold text-gray-700">{photoCount}</span>
                    <div className="text-[10px] text-gray-400">photos</div>
                    <button onClick={() => setPhotoCount(p => Math.max(0, p-1))} className="w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600">−</button>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Delivery notes</label>
                <textarea className="input text-sm resize-none" rows={2} placeholder="Overall delivery notes, storage location, batch numbers…" value={notes} onChange={e => setNotes(e.target.value)} />
              </div>

              {/* Gap 4 — Allocate to task */}
              {jobTasks.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <ClipboardList className="w-3.5 h-3.5 text-blue-600" />
                    Allocate to task
                    <span className="text-gray-400 font-normal normal-case ml-1">— which task will use these materials?</span>
                  </div>
                  <select
                    className="input text-sm w-full"
                    value={allocatedTaskId}
                    onChange={e => setAllocatedTaskId(e.target.value)}
                  >
                    <option value="">Not allocated to a specific task</option>
                    {jobTasks.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.taskNumber} — {t.title}
                        {t.bomItemIds.length > 0 ? ` (✓ ${t.bomItemIds.length} BOM item${t.bomItemIds.length !== 1 ? 's' : ''})` : ''}
                      </option>
                    ))}
                  </select>
                  {allocatedTaskId && (() => {
                    const t = jobTasks.find(t => t.id === allocatedTaskId)
                    if (!t) return null
                    const taskBomItems = t.bomItemIds.map(id => MOCK_BOM.find(b => b.id === id)).filter(Boolean)
                    return (
                      <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
                        <div className="font-semibold mb-1">Task {t.taskNumber}: {t.title}</div>
                        {taskBomItems.length > 0 && (
                          <div className="text-blue-600">
                            Linked BOM: {taskBomItems.map(b => b!.description.slice(0, 35)).join(' · ')}
                          </div>
                        )}
                        {autoMatchedTask?.id === allocatedTaskId && (
                          <div className="text-green-700 mt-1 font-medium">✓ Auto-matched from PO description</div>
                        )}
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
                <button
                  disabled={!canSubmit}
                  onClick={() => {
                    // Determine delivery location from the location string
                    const delivLoc: DeliveryLocation =
                      location.toLowerCase().includes('warehouse') ||
                      location.toLowerCase().includes('depot') ||
                      location.toLowerCase().includes('yard')
                        ? 'IN_WAREHOUSE' : 'ON_SITE'

                    // Build one DeliveryChecklistLine per GRN line
                    const checklistLines: DeliveryChecklistLine[] = lines.map((line, i) => {
                      const lineStatus: DeliveryLineStatus =
                        line.condition === 'REJECTED' ? 'REJECTED' :
                        line.condition === 'DAMAGED'  ? 'DAMAGED'  :
                        line.receivedQty < line.orderedQty ? 'PARTIAL' : 'RECEIVED'

                      // Best-effort BOM match: same job + description keyword overlap
                      const bomMatch = job
                        ? MOCK_BOM.find(b =>
                            (b as any).jobId === job.id &&
                            b.description.toLowerCase().includes(
                              line.description.toLowerCase().slice(0, 20)
                            )
                          )
                        : null

                      return {
                        lineId:           `grn-line-${Date.now()}-${i}`,
                        description:      line.description,
                        unit:             line.unit,
                        orderedQty:       line.orderedQty,
                        receivedQty:      line.receivedQty,
                        status:           lineStatus,
                        condition:        line.condition,
                        deliveryLocation: delivLoc,
                        bomLineId:        (bomMatch as any)?.id ?? null,
                        supplierPartNo:   null,
                        notes:            line.notes ?? '',
                      }
                    })

                    // Persist to the shared DeliveryConfirmation store —
                    // this writes to _bomStockOverrides so any component
                    // calling getEffectiveBOMStockStatus() sees updated status.
                    addDeliveryConfirmation({
                      id:               `dc-${po.poNumber ?? po.id}-${Date.now()}`,
                      poRef:            po.poNumber ?? po.id,
                      jobId:            job?.id ?? '',
                      jobNumber:        job?.jobNumber ?? '',
                      supplierId:       po.supplierId ?? '',
                      supplierName:     po.supplier ?? '',
                      confirmedAt:      new Date().toISOString(),
                      confirmedByName:  'Current User',
                      deliveryDocketRef: docketRef,
                      lines:            checklistLines,
                      allReceived:      !hasDiscrepancy,
                      hasDiscrepancy,
                      notes:            notes ?? '',
                    })

                    setSubmitted(true)
                  }}
                  className="btn-primary text-sm disabled:opacity-50 flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />Submit GRN
                  {allocatedTaskId && <span className="text-xs opacity-75 ml-1">+ allocate to task</span>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// STEP 4 — 3-WAY MATCH PANEL
// ─────────────────────────────────────────────────────────────────

interface ThreeWayMatchPanelProps {
  match: ThreeWayMatchResult
  canApprove: boolean
}

function MatchCell({
  label, ref: refNum, qty, unit, price, total, date, person, highlight,
}: {
  label: string; ref?: string; qty?: number; unit?: string
  price?: number; total?: number; date?: string; person?: string
  highlight?: 'ok' | 'warn' | 'err' | 'none'
}) {
  const borderColors = { ok: 'border-green-200', warn: 'border-amber-300', err: 'border-red-300', none: 'border-gray-200' }
  const bgColors     = { ok: 'bg-green-50',      warn: 'bg-amber-50',      err: 'bg-red-50',     none: 'bg-gray-50' }
  const border = borderColors[highlight ?? 'none']
  const bg     = bgColors[highlight ?? 'none']
  return (
    <div className={`border rounded-xl p-3 ${border} ${bg}`}>
      <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-2">{label}</div>
      {refNum && <div className="text-xs font-mono text-gray-400 mb-1">{refNum}</div>}
      {qty != null && unit && (
        <div className="text-sm font-bold text-gray-900">{qty} {unit}</div>
      )}
      {price != null && (
        <div className="text-xs text-gray-600 mt-0.5">{fmtAUD(price)} / unit</div>
      )}
      {total != null && (
        <div className="text-xs font-semibold text-gray-800 mt-1">= {fmtAUD(total)}</div>
      )}
      {date && <div className="text-[10px] text-gray-400 mt-2">{fmtDate(date)}</div>}
      {person && <div className="text-[10px] text-gray-400">{person}</div>}
      <div className="mt-2">
        {highlight === 'ok'   && <span className="text-[10px] text-green-700 font-semibold">✓ Match</span>}
        {highlight === 'warn' && <span className="text-[10px] text-amber-700 font-semibold">⚠ Variance</span>}
        {highlight === 'err'  && <span className="text-[10px] text-red-700 font-semibold">⚑ Mismatch</span>}
        {highlight === 'none' && refNum == null && <span className="text-[10px] text-gray-400">—</span>}
      </div>
    </div>
  )
}

export function ThreeWayMatchPanel({ match, canApprove }: ThreeWayMatchPanelProps) {
  const [showDetail, setShowDetail] = useState(true)
  const mm = MATCH_STATUS_META[match.matchStatus]

  const qtyHighlight = (side: 'po'|'grn'|'inv'): 'ok'|'warn'|'err'|'none' => {
    if (match.matchStatus === 'MISSING_GRN' || match.matchStatus === 'MISSING_PO') return 'none'
    if (match.matchStatus === 'FULL_MATCH' || match.matchStatus === 'PRICE_VARIANCE' || match.matchStatus === 'APPROVED') return 'ok'
    if (match.matchStatus === 'QUANTITY_VARIANCE') {
      if (side === 'po')  return 'ok'
      if (side === 'grn') return 'ok'
      if (side === 'inv') return (match.qtyVariance ?? 0) > 0 ? 'err' : 'ok'
    }
    return 'none'
  }

  const priceHighlight = (side: 'po'|'grn'|'inv'): 'ok'|'warn'|'err'|'none' => {
    if (match.matchStatus === 'FULL_MATCH' || match.matchStatus === 'APPROVED') return 'ok'
    if (match.matchStatus === 'PRICE_VARIANCE') {
      if (side === 'po')  return 'ok'
      if (side === 'inv') return Math.abs(match.priceVariancePct ?? 0) > 5 ? 'err' : 'warn'
      return 'none'
    }
    return 'none'
  }

  return (
    <div className={`border rounded-xl overflow-hidden ${mm.border}`}>
      {/* Status banner */}
      <div className={`px-4 py-3 flex items-center justify-between ${mm.bg}`}>
        <div className="flex items-center gap-2">
          <span className={`text-base font-bold ${mm.text}`}>{mm.icon}</span>
          <div>
            <div className={`text-xs font-bold uppercase tracking-wide ${mm.text}`}>3-way match — {mm.label}</div>
            <div className="text-[10px] text-gray-500 mt-0.5">
              {match.poRef && <span className="mr-2">PO: {match.poRef}</span>}
              {match.grnNumber && <span className="mr-2">GRN: {match.grnNumber}</span>}
              Auto-confidence: {match.autoMatchConfidencePct ?? '—'}%
            </div>
          </div>
        </div>
        <button onClick={() => setShowDetail(d => !d)} className="text-gray-400 hover:text-gray-600">
          {showDetail ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {showDetail && (
        <div className="p-4 space-y-4">
          {/* 3-column match comparison */}
          {['FULL_MATCH','PRICE_VARIANCE','QUANTITY_VARIANCE','APPROVED','DISPUTED'].includes(match.matchStatus) && (
            <div className="grid grid-cols-3 gap-2">
              <MatchCell
                label="Purchase order"
                ref={match.poRef}
                qty={match.poOrderedQty}
                unit="units"
                price={match.poUnitPrice}
                total={match.poTotal}
                date={match.poDate}
                highlight={
                  match.matchStatus === 'FULL_MATCH' || match.matchStatus === 'APPROVED' ? 'ok'
                  : match.matchStatus === 'PRICE_VARIANCE' || match.matchStatus === 'QUANTITY_VARIANCE' ? 'ok'
                  : 'none'
                }
              />
              <MatchCell
                label="Goods received (GRN)"
                ref={match.grnNumber}
                qty={match.grnReceivedQty}
                unit="units"
                total={match.grnReceivedValue}
                date={match.grnDate}
                person={match.grnReceivedBy ? `Received by: ${match.grnReceivedBy}` : undefined}
                highlight={match.grnId ? 'ok' : 'none'}
              />
              <MatchCell
                label="Invoice"
                ref={match.invoiceNumber}
                qty={match.invQty}
                unit="units"
                price={match.invUnitPrice}
                total={match.invTotal}
                date={match.invDate}
                highlight={
                  match.matchStatus === 'FULL_MATCH' || match.matchStatus === 'APPROVED' ? 'ok'
                  : match.matchStatus === 'PRICE_VARIANCE' ? (Math.abs(match.priceVariancePct ?? 0) > 5 ? 'err' : 'warn')
                  : match.matchStatus === 'QUANTITY_VARIANCE' ? ((match.qtyVariance ?? 0) > 0 ? 'err' : 'ok')
                  : 'none'
                }
              />
            </div>
          )}

          {/* Missing document states */}
          {match.matchStatus === 'MISSING_GRN' && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-gray-600">
                <div className="font-semibold text-gray-800 mb-0.5">No GRN recorded for this PO</div>
                The goods received note has not been submitted. A 3-way match cannot be performed until delivery is confirmed. Go to the Materials tab, find the PO, and click "Record receipt".
              </div>
            </div>
          )}
          {match.matchStatus === 'MISSING_PO' && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-start gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-gray-700">
                <div className="font-semibold mb-0.5">No purchase order linked to this invoice</div>
                This invoice was received without a corresponding PO. A retrospective PO must be raised and linked before payment can be approved. Unauthorised procurement — escalate to PM.
              </div>
            </div>
          )}

          {/* Variance callout */}
          {match.varianceDescription && (
            <div className={`rounded-xl px-3 py-2.5 border text-xs ${
              match.matchStatus === 'QUANTITY_VARIANCE' ? 'bg-red-50 border-red-200 text-red-800'
              : match.matchStatus === 'PRICE_VARIANCE' && (match.valueVariance ?? 0) < 0 ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-amber-50 border-amber-200 text-amber-800'
            }`}>
              <div className="font-semibold mb-1 flex items-center gap-1.5">
                {match.matchStatus === 'QUANTITY_VARIANCE' && <span>⚑</span>}
                {match.matchStatus === 'PRICE_VARIANCE' && (match.valueVariance ?? 0) < 0 && <span>↓</span>}
                {match.matchStatus === 'PRICE_VARIANCE' && (match.valueVariance ?? 0) > 0 && <span>⚠</span>}
                {match.matchStatus === 'PRICE_VARIANCE' ? 'Price variance' : 'Quantity variance'}
                {match.valueVariance != null && (
                  <span className="ml-auto font-bold">
                    {match.valueVariance < 0 ? '−' : '+'}{fmtAUD(Math.abs(match.valueVariance))} ex GST
                  </span>
                )}
              </div>
              <div>{match.varianceDescription}</div>
              {match.disputeNote && (
                <div className="mt-1.5 text-[10px] opacity-80 italic">{match.disputeNote}</div>
              )}
            </div>
          )}

          {/* Actions */}
          {!['MISSING_GRN','MISSING_PO','FULL_MATCH','APPROVED'].includes(match.matchStatus) && canApprove && (
            <div className="flex gap-2 pt-1">
              <button className="flex-1 btn-primary text-xs flex items-center justify-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                {match.matchStatus === 'PRICE_VARIANCE' && (match.valueVariance ?? 0) > 0 ? 'Approve variance — pay invoice' : 'Approve match — clear for payment'}
              </button>
              {match.matchStatus === 'QUANTITY_VARIANCE' && (
                <button className="btn-secondary text-xs flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />Dispute — hold payment
                </button>
              )}
            </div>
          )}
          {match.matchStatus === 'FULL_MATCH' && (
            <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <CheckCircle className="w-3.5 h-3.5" />All three documents agree — cleared for payment approval
            </div>
          )}
          {!canApprove && !['MISSING_GRN','MISSING_PO'].includes(match.matchStatus) && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Lock className="w-3 h-3" />Match approval requires PM sign-off
            </div>
          )}
        </div>
      )}
    </div>
  )
}
